import { useCallback, useRef } from 'react';
import * as Cesium from 'cesium';
import type { GroundStation } from '../../../types/mission';
import { ecefToGeodetic } from '../../../lib/coordinates';
import { GS_BIDIRECTIONAL_COLOR, GS_UNIDIRECTIONAL_COLOR, GS_RING_MAX_RADIUS, GS_RING_DURATION, BLACK } from '../constants';

export function useGroundStations(
  viewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  secondaryViewerRef: React.MutableRefObject<Cesium.Viewer | null>,
  sharedDataSourceRef: React.MutableRefObject<any>,
  highlightDataSourceRef: React.MutableRefObject<any>,
  entityGroupMap: React.MutableRefObject<Map<string, string[]>>,
  gsActiveMap: React.MutableRefObject<Record<string, boolean>>,
  gsImagePropsRef: React.MutableRefObject<Map<string, Cesium.ConstantProperty>>,
  gsConnectionTypeRef: React.MutableRefObject<Map<string, 'bidirectional' | 'unidirectional'>>,
  layersRef: React.MutableRefObject<any>,
  isSurfacePointCameraFacing: (lon: number, lat: number, alt: number) => boolean,
) {

  const addOneGroundStation = useCallback((gs: GroundStation) => {
    const ds = sharedDataSourceRef.current;
    if (!ds) return;

    let { id, name, lat, lon, minElevation = 5, x, y, z } = gs;
    if ((lat == null || lon == null || (lat === 0 && lon === 0)) && x != null && y != null && z != null && (x !== 0 || y !== 0 || z !== 0)) {
      const geo = ecefToGeodetic(x, y, z);
      lat = geo.lat;
      lon = geo.lon;
    }
    gsActiveMap.current[id] = gs.active !== false;

    const hasPoint = !!ds.entities.getById(`gs_point_${id}`);

    if (!hasPoint) {
      const gsImageProp = new Cesium.ConstantProperty('/assets/gs_enabled.svg');
      gsImagePropsRef.current.set(id, gsImageProp);
      ds.entities.add({
        id: `gs_point_${id}`,
        position: Cesium.Cartesian3.fromDegrees(lon, lat, 20),
        billboard: {
          image: gsImageProp,
          scale: 1.18,
          verticalOrigin: Cesium.VerticalOrigin.CENTER,
          pixelOffset: Cesium.Cartesian2.ZERO,
          heightReference: new Cesium.CallbackProperty(() => {
            const v = (window as any).currentRenderingViewer || viewerRef.current;
            if (v && v.scene.mode !== Cesium.SceneMode.SCENE3D) {
              return Cesium.HeightReference.NONE;
            }
            return Cesium.HeightReference.CLAMP_TO_GROUND;
          }, false),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          show: new Cesium.CallbackProperty(
            () => layersRef.current.ground && gsActiveMap.current[id] !== false && isSurfacePointCameraFacing(lon, lat, 20),
            false,
          ),
        },
        label: {
          text: name,
          font: '10px "Courier New", monospace',
          fillColor: Cesium.Color.WHITE,
          outlineColor: BLACK,
          outlineWidth: 2,
          style: Cesium.LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
          pixelOffset: new Cesium.Cartesian2(0, -15),
          show: new Cesium.CallbackProperty(
            () => layersRef.current.ground && layersRef.current.labels && gsActiveMap.current[id] !== false && isSurfacePointCameraFacing(lon, lat, 20),
            false,
          ),
        },
      });
    }

    const removeExistingFovEntities = (entities: Cesium.EntityCollection) => {
      const existingFovEntities = entities.values.filter(e =>
        e.id?.startsWith(`gs_fov_${id}`) ||
        e.id?.startsWith(`gs_fov_2d_${id}`) ||
        e.id?.startsWith(`gs_fov_outline_${id}`) ||
        e.id?.startsWith(`gs_fov_outline_2d_${id}`) ||
        e.id?.startsWith(`gs_fov_3d_${id}`) ||
        e.id?.startsWith(`gs_fov_outline_3d_${id}`) ||
        e.id?.startsWith(`gs_fov_nadir_${id}`) ||
        e.id?.startsWith(`gs_cone_${id}_`)
      );
      existingFovEntities.forEach(e => entities.remove(e));
    };

    removeExistingFovEntities(ds.entities);
    if (viewerRef.current) removeExistingFovEntities(viewerRef.current.entities);
    if (secondaryViewerRef.current && !secondaryViewerRef.current.isDestroyed()) {
      removeExistingFovEntities(secondaryViewerRef.current.entities);
    }

    if (lat == null || lon == null || isNaN(lat) || isNaN(lon)) return;

    const gsAltM = (gs.altKm ?? 0) * 1000;
    const REF_ALT_M = 500_000;
    const fovGeometry = gs.fovGeometry ?? 'conic';
    const pointingAz = gs.pointingAz ?? 0;
    const pointingEle = gs.pointingEle ?? 90;

    let topSemiMajor = 0;
    let topSemiMinor = 0;

    if (fovGeometry === 'conic') {
      const fovDeg = gs.fovDeg ?? (180 - minElevation * 2);
      const halfConeRad = Cesium.Math.toRadians(Math.min(fovDeg / 2, 80));
      topSemiMajor = REF_ALT_M * Math.tan(halfConeRad);
      topSemiMinor = topSemiMajor;
    } else {
      let fovH = gs.fovH;
      if (fovH == null || isNaN(Number(fovH)) || Number(fovH) <= 0) fovH = 1;
      let fovV = gs.fovV;
      if (fovV == null || isNaN(Number(fovV)) || Number(fovV) <= 0) fovV = 1;

      const halfHRad = Cesium.Math.toRadians(Math.min(fovH / 2, 80));
      const halfVRad = Cesium.Math.toRadians(Math.min(fovV / 2, 80));
      topSemiMajor = REF_ALT_M * Math.tan(halfHRad);
      topSemiMinor = REF_ALT_M * Math.tan(halfVRad);
    }

    const swapRequired = fovGeometry !== 'rectangular' && topSemiMajor < topSemiMinor;
    const finalSemiMajor = swapRequired ? topSemiMinor : topSemiMajor;
    const finalSemiMinor = swapRequired ? topSemiMajor : topSemiMinor;
    const rotOffset = swapRequired ? 90 : 0;

    const fovColor = new Cesium.Color(204 / 255, 245 / 255, 78 / 255, 0.6);

    const addFovForViewer = (v: Cesium.Viewer, isSecondary: boolean) => {
      const suffix = isSecondary ? '_sec' : '_prim';

      const show2DFovCb = new Cesium.CallbackProperty(() => {
        const is2D = v && v.scene.mode !== Cesium.SceneMode.SCENE3D;
        return !!(is2D && layersRef.current.gsFov && gsActiveMap.current[id] !== false);
      }, false);

      const show3DFovCb = new Cesium.CallbackProperty(() => {
        const is3D = v && v.scene.mode === Cesium.SceneMode.SCENE3D;
        return !!(is3D && layersRef.current.gsFov && gsActiveMap.current[id] !== false);
      }, false);

      // ─── 2D FOV entities ───
      if (fovGeometry === 'rectangular') {
        const center = Cesium.Cartesian3.fromDegrees(lon, lat, 0);
        const hpr = new Cesium.HeadingPitchRoll(Cesium.Math.toRadians(pointingAz), 0, 0);
        const localToWorldMatrix = Cesium.Transforms.headingPitchRollToFixedFrame(center, hpr);
        const corners = [
          new Cesium.Cartesian3(-topSemiMajor, -topSemiMinor, 0),
          new Cesium.Cartesian3(topSemiMajor, -topSemiMinor, 0),
          new Cesium.Cartesian3(topSemiMajor, topSemiMinor, 0),
          new Cesium.Cartesian3(-topSemiMajor, topSemiMinor, 0),
        ];
        const worldCorners = corners.map(c => Cesium.Matrix4.multiplyByPoint(localToWorldMatrix, c, new Cesium.Cartesian3()));

        v.entities.add({
          id: `gs_fov_2d_${id}${suffix}`,
          polygon: {
            hierarchy: new Cesium.PolygonHierarchy(worldCorners),
            material: new Cesium.ColorMaterialProperty(fovColor.withAlpha(0.18)),
            outline: false,
            height: 0,
            heightReference: Cesium.HeightReference.NONE,
            show: show2DFovCb,
            shadows: Cesium.ShadowMode.DISABLED,
          },
        });

        v.entities.add({
          id: `gs_fov_outline_2d_${id}${suffix}`,
          polyline: {
            positions: new Cesium.ConstantProperty([...worldCorners, worldCorners[0]]),
            arcType: Cesium.ArcType.NONE,
            width: 1.0,
            material: new Cesium.ColorMaterialProperty(fovColor.withAlpha(0.9)),
            show: show2DFovCb,
          },
        });
      } else {
        v.entities.add({
          id: `gs_fov_2d_${id}${suffix}`,
          position: Cesium.Cartesian3.fromDegrees(lon, lat),
          ellipse: {
            semiMajorAxis: finalSemiMajor,
            semiMinorAxis: finalSemiMinor,
            material: new Cesium.ColorMaterialProperty(fovColor.withAlpha(0.18)),
            outline: false,
            rotation: Cesium.Math.toRadians(pointingAz + rotOffset),
            height: 0,
            heightReference: Cesium.HeightReference.NONE,
            show: show2DFovCb,
            shadows: Cesium.ShadowMode.DISABLED,
          },
        });

        const center = Cesium.Cartesian3.fromDegrees(lon, lat, 0);
        const hpr = new Cesium.HeadingPitchRoll(Cesium.Math.toRadians(pointingAz + rotOffset), 0, 0);
        const localToWorldMatrix2D = Cesium.Transforms.headingPitchRollToFixedFrame(center, hpr);
        const perimPts2D: Cesium.Cartesian3[] = [];
        const N_PTS = 48;
        for (let pi = 0; pi <= N_PTS; pi++) {
          const a = (pi / N_PTS) * Math.PI * 2;
          const c = new Cesium.Cartesian3(
            finalSemiMajor * Math.cos(a),
            finalSemiMinor * Math.sin(a),
            0
          );
          perimPts2D.push(Cesium.Matrix4.multiplyByPoint(localToWorldMatrix2D, c, new Cesium.Cartesian3()));
        }

        v.entities.add({
          id: `gs_fov_outline_2d_${id}${suffix}`,
          polyline: {
            positions: new Cesium.ConstantProperty(perimPts2D),
            arcType: Cesium.ArcType.NONE,
            width: 1.0,
            material: new Cesium.ColorMaterialProperty(fovColor.withAlpha(0.9)),
            show: show2DFovCb,
          },
        });
      }

      // ─── 3D FOV entities ───
      const apex = Cesium.Cartesian3.fromDegrees(lon, lat, gsAltM);

      const heading = Cesium.Math.toRadians(pointingAz);
      const pitch = Cesium.Math.toRadians(pointingEle - 90);
      const finalHeading = heading + Cesium.Math.toRadians(rotOffset);
      const finalHpr = new Cesium.HeadingPitchRoll(finalHeading, pitch, 0);
      const localToWorldMatrix = Cesium.Transforms.headingPitchRollToFixedFrame(apex, finalHpr);

      const localCenter = new Cesium.Cartesian3(0, 0, REF_ALT_M);
      const topCenterWorld = Cesium.Matrix4.multiplyByPoint(localToWorldMatrix, localCenter, new Cesium.Cartesian3());

      const isRect = fovGeometry === 'rectangular';
      const N_CONE = isRect ? 4 : 48;
      const perimPts: Cesium.Cartesian3[] = [];

      if (isRect) {
        const corners = [
          new Cesium.Cartesian3(-topSemiMajor, -topSemiMinor, REF_ALT_M),
          new Cesium.Cartesian3(topSemiMajor, -topSemiMinor, REF_ALT_M),
          new Cesium.Cartesian3(topSemiMajor, topSemiMinor, REF_ALT_M),
          new Cesium.Cartesian3(-topSemiMajor, topSemiMinor, REF_ALT_M),
          new Cesium.Cartesian3(-topSemiMajor, -topSemiMinor, REF_ALT_M),
        ];
        corners.forEach(c => {
          perimPts.push(Cesium.Matrix4.multiplyByPoint(localToWorldMatrix, c, new Cesium.Cartesian3()));
        });
      } else {
        for (let pi = 0; pi <= N_CONE; pi++) {
          const a = (pi / N_CONE) * Math.PI * 2;
          const c = new Cesium.Cartesian3(
            topSemiMajor * Math.cos(a),
            topSemiMinor * Math.sin(a),
            REF_ALT_M
          );
          perimPts.push(Cesium.Matrix4.multiplyByPoint(localToWorldMatrix, c, new Cesium.Cartesian3()));
        }
      }

      const rayCount = isRect ? 4 : N_CONE;
      for (let ci = 0; ci < rayCount; ci++) {
        v.entities.add({
          id: `gs_cone_${id}_${ci}${suffix}`,
          polyline: {
            positions: new Cesium.ConstantProperty([apex, perimPts[ci]]),
            arcType: Cesium.ArcType.NONE,
            width: 0.6,
            material: new Cesium.ColorMaterialProperty(fovColor.withAlpha(0.30)),
            show: show3DFovCb,
          },
        });
      }

      if (isRect) {
        v.entities.add({
          id: `gs_fov_3d_${id}${suffix}`,
          polygon: {
            hierarchy: new Cesium.PolygonHierarchy(perimPts.slice(0, 4)),
            material: new Cesium.ColorMaterialProperty(fovColor.withAlpha(0.18)),
            outline: false,
            show: show3DFovCb,
            shadows: Cesium.ShadowMode.DISABLED,
          },
        });
      } else {
        const orient = Cesium.Transforms.headingPitchRollQuaternion(apex, finalHpr);
        const topCenterCarto = Cesium.Cartographic.fromCartesian(topCenterWorld);
        const topHeight = topCenterCarto ? topCenterCarto.height : gsAltM + REF_ALT_M;
        v.entities.add({
          id: `gs_fov_3d_${id}${suffix}`,
          position: topCenterWorld,
          orientation: new Cesium.ConstantProperty(orient),
          ellipse: {
            semiMajorAxis: finalSemiMajor,
            semiMinorAxis: finalSemiMinor,
            material: new Cesium.ColorMaterialProperty(fovColor.withAlpha(0.18)),
            outline: false,
            show: show3DFovCb,
            height: topHeight,
            shadows: Cesium.ShadowMode.DISABLED,
          },
        });
      }

      v.entities.add({
        id: `gs_fov_outline_3d_${id}${suffix}`,
        polyline: {
          positions: new Cesium.ConstantProperty(perimPts),
          arcType: Cesium.ArcType.NONE,
          width: 0.8,
          material: new Cesium.ColorMaterialProperty(fovColor.withAlpha(0.9)),
          show: show3DFovCb,
        },
      });
    };

    if (viewerRef.current) {
      addFovForViewer(viewerRef.current, false);
    }
    if (secondaryViewerRef.current && !secondaryViewerRef.current.isDestroyed()) {
      addFovForViewer(secondaryViewerRef.current, true);
    }
  }, [isSurfacePointCameraFacing]);

  const removeGroundStation = useCallback((id: string) => {
    const ds = sharedDataSourceRef.current;
    if (!ds) return;
    const removeMatching = (entities: Cesium.EntityCollection) => {
      const toRemove = entities.values.filter(e =>
        e.id?.startsWith(`gs_point_${id}`) ||
        e.id?.startsWith(`gs_fov_${id}`) ||
        e.id?.startsWith(`gs_fov_outline_${id}`) ||
        e.id?.startsWith(`gs_fov_nadir_${id}`) ||
        e.id?.startsWith(`gs_cone_${id}_`)
      );
      toRemove.forEach(e => entities.remove(e));
    };

    removeMatching(ds.entities);
    if (viewerRef.current) removeMatching(viewerRef.current.entities);
    if (secondaryViewerRef.current && !secondaryViewerRef.current.isDestroyed()) {
      removeMatching(secondaryViewerRef.current.entities);
    }
    delete gsActiveMap.current[id];
  }, []);

  const addGroundStations = useCallback((stations: GroundStation[]) => {
    stations.forEach(gs => addOneGroundStation(gs));
  }, [addOneGroundStation]);

  const updateOneGroundStation = useCallback((gs: GroundStation) => {
    removeGroundStation(gs.id);
    addOneGroundStation(gs);
  }, [removeGroundStation, addOneGroundStation]);

  const lastDraftPropsRef = useRef<any>(null);

  const removeDraftGroundStation = useCallback(() => {
    lastDraftPropsRef.current = null;
    const v = viewerRef.current;
    if (!v) return;
    const toRemove = v.entities.values.filter(e =>
      e.id?.startsWith('__draft_gs_')
    );
    toRemove.forEach(e => v.entities.remove(e));
  }, []);

  const redrawDraftGroundStation = useCallback(() => {
    if (lastDraftPropsRef.current) {
      const props = lastDraftPropsRef.current;
      const v = viewerRef.current;
      if (!v) return;

      // Clear old draft entities manually without clearing the ref
      const toRemove = v.entities.values.filter(e =>
        e.id?.startsWith('__draft_gs_')
      );
      toRemove.forEach(e => v.entities.remove(e));

      // Re-call updateDraftGroundStation
      updateDraftGroundStation(props);
    }
  }, []); // eslint-disable-next-line react-hooks/exhaustive-deps

  const removeGsRingEntities = (gsId: string) => {
    const ds = sharedDataSourceRef.current;
    if (!ds) return;
    const toRemove = ds.entities.values.filter(e => {
      const id = e.id?.toString() ?? '';
      return id === `${gsId}_fill` || id.startsWith(`${gsId}_inner_ring_`);
    });
    toRemove.forEach(e => ds.entities.remove(e));
  };

  const updateDraftGroundStation = useCallback((gsInput: Partial<any> | Partial<any>[]) => {
    lastDraftPropsRef.current = gsInput;
    const v = viewerRef.current;
    if (!v) return;

    // Clear old draft entities manually without clearing the ref
    const toRemove = v.entities.values.filter(e =>
      e.id?.startsWith('__draft_gs_')
    );
    toRemove.forEach(e => v.entities.remove(e));

    const gsList = Array.isArray(gsInput) ? gsInput : [gsInput];

    gsList.forEach((gs, index) => {
      const { lat, lon } = gs;
      if (lat == null || lon == null || isNaN(lat) || isNaN(lon)) return;

      const suffix = Array.isArray(gsInput) ? `_${index}` : '';

      const minElevation = gs.minElevation ?? 5;
      const gsAltM = (gs.altKm ?? 0) * 1000;
      const REF_ALT_M = 500_000;
      const fovGeometry = gs.fovGeometry ?? 'conic';

      let topSemiMajor = 0;
      let topSemiMinor = 0;

      if (fovGeometry === 'conic') {
        const fovDeg = gs.fovDeg ?? (180 - minElevation * 2);
        const halfConeRad = Cesium.Math.toRadians(Math.min(fovDeg / 2, 80));
        topSemiMajor = REF_ALT_M * Math.tan(halfConeRad);
        topSemiMinor = topSemiMajor;
      } else {
        let fovH = gs.fovH;
        if (fovH == null || isNaN(Number(fovH)) || Number(fovH) <= 0) fovH = 1;
        let fovV = gs.fovV;
        if (fovV == null || isNaN(Number(fovV)) || Number(fovV) <= 0) fovV = 1;

        const halfHRad = Cesium.Math.toRadians(Math.min(fovH / 2, 80));
        const halfVRad = Cesium.Math.toRadians(Math.min(fovV / 2, 80));
        topSemiMajor = REF_ALT_M * Math.tan(halfHRad);
        topSemiMinor = REF_ALT_M * Math.tan(halfVRad);
      }

      const swapRequired = fovGeometry !== 'rectangular' && topSemiMajor < topSemiMinor;
      const finalSemiMajor = swapRequired ? topSemiMinor : topSemiMajor;
      const finalSemiMinor = swapRequired ? topSemiMajor : topSemiMinor;
      const rotOffset = swapRequired ? 90 : 0;

      const fovColor = new Cesium.Color(204 / 255, 245 / 255, 78 / 255, 0.6);

      v.entities.add({
        id: `__draft_gs_point${suffix}__`,
        position: Cesium.Cartesian3.fromDegrees(lon, lat),
        point: {
          pixelSize: 7,
          color: fovColor,
          outlineColor: BLACK,
          outlineWidth: 1,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          show: new Cesium.CallbackProperty(() => isSurfacePointCameraFacing(lon, lat, gsAltM), false),
        },
      });

      const is2D = v.scene.mode !== Cesium.SceneMode.SCENE3D;
      const pointingAz = gs.pointingAz ?? 0;
      const pointingEle = gs.pointingEle ?? 90;

      if (is2D) {
        if (fovGeometry === 'rectangular') {
          const center = Cesium.Cartesian3.fromDegrees(lon, lat, 0);
          const hpr = new Cesium.HeadingPitchRoll(Cesium.Math.toRadians(pointingAz), 0, 0);
          const localToWorldMatrix = Cesium.Transforms.headingPitchRollToFixedFrame(center, hpr);
          const corners = [
            new Cesium.Cartesian3(-topSemiMajor, -topSemiMinor, 0),
            new Cesium.Cartesian3(topSemiMajor, -topSemiMinor, 0),
            new Cesium.Cartesian3(topSemiMajor, topSemiMinor, 0),
            new Cesium.Cartesian3(-topSemiMajor, topSemiMinor, 0),
          ];
          const worldCorners = corners.map(c => Cesium.Matrix4.multiplyByPoint(localToWorldMatrix, c, new Cesium.Cartesian3()));

          v.entities.add({
            id: `__draft_gs_fov${suffix}__`,
            polygon: {
              hierarchy: new Cesium.PolygonHierarchy(worldCorners),
              material: new Cesium.ColorMaterialProperty(fovColor.withAlpha(0.18)),
              outline: false,
              height: 0,
              heightReference: Cesium.HeightReference.NONE,
              show: new Cesium.CallbackProperty(() => isSurfacePointCameraFacing(lon, lat, gsAltM), false),
              shadows: Cesium.ShadowMode.DISABLED,
            },
          });

          // Add a closed loop polyline for outline with ArcType.NONE to avoid geodesic curvature distortion
          v.entities.add({
            id: `__draft_gs_fov_outline${suffix}__`,
            polyline: {
              positions: new Cesium.ConstantProperty([...worldCorners, worldCorners[0]]),
              arcType: Cesium.ArcType.NONE,
              width: 1.0,
              material: new Cesium.ColorMaterialProperty(fovColor.withAlpha(0.9)),
              show: new Cesium.CallbackProperty(() => isSurfacePointCameraFacing(lon, lat, gsAltM), false),
            },
          });
        } else {
          v.entities.add({
            id: `__draft_gs_fov${suffix}__`,
            position: Cesium.Cartesian3.fromDegrees(lon, lat),
            ellipse: {
              semiMajorAxis: finalSemiMajor,
              semiMinorAxis: finalSemiMinor,
              material: new Cesium.ColorMaterialProperty(fovColor.withAlpha(0.18)),
              outline: true,
              outlineColor: fovColor.withAlpha(0.9),
              outlineWidth: 1.0,
              rotation: Cesium.Math.toRadians(pointingAz + rotOffset),
              height: 0,
              heightReference: Cesium.HeightReference.NONE,
              show: new Cesium.CallbackProperty(() => isSurfacePointCameraFacing(lon, lat, gsAltM), false),
              shadows: Cesium.ShadowMode.DISABLED,
            },
          });
        }
      } else {
        const draftApex = Cesium.Cartesian3.fromDegrees(lon, lat, gsAltM);

        const heading = Cesium.Math.toRadians(pointingAz);
        const pitch = Cesium.Math.toRadians(pointingEle - 90);
        const finalHeading = heading + Cesium.Math.toRadians(rotOffset);
        const finalHpr = new Cesium.HeadingPitchRoll(finalHeading, pitch, 0);
        const localToWorldMatrix = Cesium.Transforms.headingPitchRollToFixedFrame(draftApex, finalHpr);

        const localCenter = new Cesium.Cartesian3(0, 0, REF_ALT_M);
        const topCenterWorld = Cesium.Matrix4.multiplyByPoint(localToWorldMatrix, localCenter, new Cesium.Cartesian3());

        const isRect = fovGeometry === 'rectangular';
        const N_DRAFT_CONE = isRect ? 4 : 48;
        const draftPerimPts: Cesium.Cartesian3[] = [];

        if (isRect) {
          const corners = [
            new Cesium.Cartesian3(-topSemiMajor, -topSemiMinor, REF_ALT_M),
            new Cesium.Cartesian3(topSemiMajor, -topSemiMinor, REF_ALT_M),
            new Cesium.Cartesian3(topSemiMajor, topSemiMinor, REF_ALT_M),
            new Cesium.Cartesian3(-topSemiMajor, topSemiMinor, REF_ALT_M),
            new Cesium.Cartesian3(-topSemiMajor, -topSemiMinor, REF_ALT_M),
          ];
          corners.forEach(c => {
            draftPerimPts.push(Cesium.Matrix4.multiplyByPoint(localToWorldMatrix, c, new Cesium.Cartesian3()));
          });
        } else {
          for (let pi = 0; pi <= N_DRAFT_CONE; pi++) {
            const a = (pi / N_DRAFT_CONE) * Math.PI * 2;
            const c = new Cesium.Cartesian3(
              topSemiMajor * Math.cos(a),
              topSemiMinor * Math.sin(a),
              REF_ALT_M
            );
            draftPerimPts.push(Cesium.Matrix4.multiplyByPoint(localToWorldMatrix, c, new Cesium.Cartesian3()));
          }
        }

        const rayCount = isRect ? 4 : N_DRAFT_CONE;
        for (let ci = 0; ci < rayCount; ci++) {
          v.entities.add({
            id: `__draft_gs_cone_${ci}${suffix}__`,
            polyline: {
              positions: new Cesium.ConstantProperty([draftApex, draftPerimPts[ci]]),
              arcType: Cesium.ArcType.NONE,
              width: 0.6,
              material: new Cesium.ColorMaterialProperty(fovColor.withAlpha(0.30)),
              show: new Cesium.CallbackProperty(() => isSurfacePointCameraFacing(lon, lat, gsAltM), false),
            },
          });
        }

        if (isRect) {
          v.entities.add({
            id: `__draft_gs_fov${suffix}__`,
            polygon: {
              hierarchy: new Cesium.PolygonHierarchy(draftPerimPts.slice(0, 4)),
              material: new Cesium.ColorMaterialProperty(fovColor.withAlpha(0.18)),
              outline: false,
              show: new Cesium.CallbackProperty(() => isSurfacePointCameraFacing(lon, lat, gsAltM), false),
              shadows: Cesium.ShadowMode.DISABLED,
            },
          });
        } else {
          const orient = Cesium.Transforms.headingPitchRollQuaternion(draftApex, finalHpr);
          const topCenterCarto = Cesium.Cartographic.fromCartesian(topCenterWorld);
          const topHeight = topCenterCarto ? topCenterCarto.height : gsAltM + REF_ALT_M;
          v.entities.add({
            id: `__draft_gs_fov${suffix}__`,
            position: topCenterWorld,
            orientation: new Cesium.ConstantProperty(orient),
            ellipse: {
              semiMajorAxis: finalSemiMajor,
              semiMinorAxis: finalSemiMinor,
              material: new Cesium.ColorMaterialProperty(fovColor.withAlpha(0.18)),
              outline: false,
              show: new Cesium.CallbackProperty(() => isSurfacePointCameraFacing(lon, lat, gsAltM), false),
              height: topHeight,
              shadows: Cesium.ShadowMode.DISABLED,
            },
          });
        }

        v.entities.add({
          id: `__draft_gs_fov_outline${suffix}__`,
          polyline: {
            positions: new Cesium.ConstantProperty(draftPerimPts),
            arcType: Cesium.ArcType.NONE,
            width: 0.8,
            material: new Cesium.ColorMaterialProperty(fovColor.withAlpha(0.9)),
            show: new Cesium.CallbackProperty(() => isSurfacePointCameraFacing(lon, lat, gsAltM), false),
          },
        });
      }
    });
  }, [removeDraftGroundStation]);

  const displayGsContactRings = useCallback((
    groundStations: GroundStation[],
    contactGsIds: Set<string>,
    connectionTypes: Map<string, 'bidirectional' | 'unidirectional'> = new Map()
  ) => {
    if (!groundStations?.length) return;

    const animStartMs = Date.now();
    groundStations.forEach(station => {
      if (!contactGsIds.has(station.id)) {
        removeGsRingEntities(station.id);
        const imageProp = gsImagePropsRef.current.get(station.id);
        if (imageProp) {
          imageProp.setValue('/assets/gs_enabled.svg');
        }
        delete gsActiveMap.current[`${station.id}_connected`];
        return;
      }
      if (!station.lon || !station.lat) return;

      const connectionType = connectionTypes.get(station.id) ?? 'bidirectional';
      gsActiveMap.current[`${station.id}_connected`] = true;
      gsConnectionTypeRef.current.set(station.id, connectionType);
      const imageProp = gsImagePropsRef.current.get(station.id);
      if (imageProp && !gsActiveMap.current[`${station.id}_hover`]) {
        // Keep the normal ground station dish icon always
        imageProp.setValue('/assets/gs_enabled.svg');
      }
      const ringColor = connectionType === 'bidirectional' ? GS_BIDIRECTIONAL_COLOR : GS_UNIDIRECTIONAL_COLOR;
      addGsRingsForPosition(station.id, station.lon, station.lat, animStartMs, ringColor);
    });
  }, []);

  const clearAllGsRings = useCallback(() => {
    const ds = sharedDataSourceRef.current;
    if (!ds) return;
    const toRemove = ds.entities.values.filter(e => {
      const id = e.id?.toString() ?? '';
      return id.endsWith('_fill') || /_inner_ring_\d+$/.test(id);
    });
    toRemove.forEach(e => ds.entities.remove(e));
  }, []);

  const addGsRingsForPosition = useCallback((
    gsId: string,
    lon: number,
    lat: number,
    animStartMs: number,
    ringColor: string = GS_BIDIRECTIONAL_COLOR
  ) => {
    const v = viewerRef.current;
    if (!v) return;

    removeGsRingEntities(gsId);
    const center = Cesium.Cartesian3.fromDegrees(lon, lat, 0);

    const RINGS = [
      { radius: GS_RING_MAX_RADIUS, phase: 0.04, baseAlpha: 0.15, peakAlpha: 0.25, height: 0 },
      { radius: GS_RING_MAX_RADIUS * 0.75, phase: 0.06, baseAlpha: 0.20, peakAlpha: 0.30, height: 100 },
      { radius: GS_RING_MAX_RADIUS * 0.50, phase: 0.08, baseAlpha: 0.25, peakAlpha: 0.35, height: 200 },
    ];

    RINGS.forEach(({ radius, phase, baseAlpha, peakAlpha, height }, i) => {
      const id = i === 0 ? `${gsId}_fill` : `${gsId}_inner_ring_${i - 1}`;
      sharedDataSourceRef.current?.entities.add({
        id,
        position: center,
        ellipse: {
          semiMinorAxis: radius,
          semiMajorAxis: radius,
          height: (v && v.scene.mode !== Cesium.SceneMode.SCENE3D) ? 0 : height + 50,
          granularity: Cesium.Math.toRadians(0.5),
          material: new Cesium.ColorMaterialProperty(
            new Cesium.CallbackProperty(() => {
              const elapsed = (Date.now() - animStartMs) / 1000;
              const raw = (elapsed / GS_RING_DURATION) + phase;
              const t = raw - Math.floor(raw);
              const alpha = baseAlpha + (peakAlpha - baseAlpha) * Math.sin(t * Math.PI);
              const color = Cesium.Color.fromCssColorString(ringColor);
              return color.withAlpha(alpha);
            }, false)
          ),
          fill: true,
          outline: i === 0,
          outlineColor: new Cesium.CallbackProperty(() => {
            const elapsed = (Date.now() - animStartMs) / 1000;
            const raw = (elapsed / GS_RING_DURATION) + phase;
            const t = raw - Math.floor(raw);
            const alpha = baseAlpha + (peakAlpha - baseAlpha) * Math.sin(t * Math.PI);
            return Cesium.Color.fromCssColorString(ringColor).withAlpha(alpha);
          }, false),
          outlineWidth: 1.5,
          zIndex: 100,
        }
      });
    });
  }, []);

  return {
    addGroundStations,
    addOneGroundStation,
    removeGroundStation,
    updateOneGroundStation,
    removeDraftGroundStation,
    updateDraftGroundStation,
    redrawDraftGroundStation,
    displayGsContactRings,
    clearAllGsRings,
    addGsRingsForPosition,
  };
}
